import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {updateProviderProfile, getProviderForEdit} from '../../services/providersApi';
import { useCategories } from '../../utils/CategoriesContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './EditProviderProfile.css';
import ProgressIndicator from "./EditionComponents/ProgressIndicator";
import StepNavigation from "./EditionComponents/StepNavigation";
import BasicInfoStep from "./EditionComponents/BasicInfoStep";
import ContactStep from "./EditionComponents/ContactStep";
import CategoriesStep from "./EditionComponents/CategoriesStep";
import MediaStep from "./EditionComponents/MediaStep";
import PreviewStep from "./EditionComponents/PreviewStep";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {getUserData} from "../../utils/auth";
import Spinner from "../Spinner/Spinner";
import {
    prepareProviderDataForBackend,
    debugProviderFormat,
    getProviderCategories
} from '../../utils/migrationHelpers'; // 🎯 Helpers de migración

const STEPS = [
    { id: 'basic', title: 'Información Básica' },
    { id: 'contact', title: 'Contacto' },
    { id: 'categories', title: 'Categorías' },
    { id: 'media', title: 'Fotos' },
    { id: 'preview', title: 'Vista Previa' }
];

const EditProviderProfile = () => {
    const { providerId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { getCategoriesByType } = useCategories();

    // Estados principales
    const [initialData, setInitialData] = useState({});
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);

    // Catálogo de categorías disponibles — leído directamente del context
    // prefetcheado en cada render (mismo patrón que RegisterProviderProfile),
    // así no quedamos atrás cuando el context termina de cargar.
    const categories = useMemo(() => {
        const type = formData?.type || getUserData()?.providerType;
        if (!type) return [];
        return getCategoriesByType(type) || [];
    }, [formData?.type, getCategoriesByType]);
    const [saving, setSaving] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [errors, setErrors] = useState({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isActive, setIsActive] = useState(false); // Estado para controlar si el perfil está activo

    // Capturar la página desde la cual se navegó para volver después de editar
    const previousPage = location.state?.from || null;

    // Aplicar colores dinámicos según tipo de proveedor
    useEffect(() => {
        if (formData.type) {
            const root = document.documentElement;
            if (formData.type === 'products') {
                // Azul para productos
                root.style.setProperty('--primary-color', '#2196f3');
                root.style.setProperty('--primary-hover', '#1976d2');
            } else {
                // Rojo para servicios
                root.style.setProperty('--primary-color', '#f44336');
                root.style.setProperty('--primary-hover', '#d32f2f');
            }
        }

        return () => {
            // Restaurar colores por defecto al salir
            const root = document.documentElement;
            root.style.setProperty('--primary-color', '#bb86fc');
            root.style.setProperty('--primary-hover', '#9c6dfd');
        };
    }, [formData.type]);

    // Validación en tiempo real
    const validateField = useCallback((field, value) => {
        const fieldErrors = {};

        switch (field) {
            case 'name':
                if (!value || value.trim() === "") {
                    fieldErrors.name = "El nombre es requerido";
                } else if (value.length > 100) {
                    fieldErrors.name = "El nombre no puede exceder 100 caracteres";
                }
                break;
            case 'description':
                if (!value || value.trim() === "") {
                    fieldErrors.description = "La descripción es requerida";
                } else if (value.length > 1000) {
                    fieldErrors.description = "La descripción no puede exceder 1000 caracteres";
                }
                break;
            case 'phone':
                if (!value || value.trim() === "") {
                    fieldErrors.phone = "El teléfono es requerido";
                } else if (!/^\d+$/.test(value)) {
                    fieldErrors.phone = "El teléfono debe contener solo números";
                } else if (value.length < 8 || value.length > 15) {
                    fieldErrors.phone = "El teléfono debe tener entre 8 y 15 dígitos";
                }
                break;
            case 'categories':
                if (!value || value.length === 0) {
                    fieldErrors.categories = "Debes seleccionar al menos una categoría";
                }
                break;
            case 'locations':
                if (!value || value.length === 0) {
                    fieldErrors.locations = "Debes seleccionar al menos una ubicación";
                }
                break;
            case 'photo':
                if (!value || value.trim() === "") {
                    fieldErrors.photo = "La foto de perfil es requerida";
                }
                break;
        }

        setErrors(prev => {
            const newErrors = { ...prev };
            if (Object.keys(fieldErrors).length === 0) {
                delete newErrors[field];
            } else {
                newErrors[field] = fieldErrors[field];
            }
            return newErrors;
        });

        return Object.keys(fieldErrors).length === 0;
    }, []);

    // Calcular progreso del perfil
    const profileProgress = useMemo(() => {
        if (!formData || Object.keys(formData).length === 0) return 0;

        const requiredFields = ['name', 'description', 'phone', 'categories', 'locations', 'photo'];
        const completedFields = requiredFields.filter(field => {
            const value = formData[field];
            if (Array.isArray(value)) return value.length > 0;
            return value && value.toString().trim() !== "";
        });
        return Math.round((completedFields.length / requiredFields.length) * 100);
    }, [formData]);

    // Cargar datos iniciales
    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                const userData = getUserData();
                if (!userData || !userData.providerRef || providerId !== userData.providerRef) {
                    toast.error("No tienes permiso para editar este perfil");
                    setTimeout(() => navigate(`/${providerId}`), 1500);
                    return;
                }

                const provider = await getProviderForEdit(providerId);

                if (isMounted) {
                    // Convertir los datos del proveedor al formato esperado por CategoriesStep
                    const providerCategories = getProviderCategories(provider);
                    const adaptedProviderData = {
                        ...provider,
                        // CategoriesStep espera categories como nombres, no IDs
                        categories: providerCategories,
                        // Mantener subcategories en formato categoryId-subcategory
                        subcategories: provider.subcategories || []
                    };


                    setInitialData(provider);
                    setFormData(adaptedProviderData);
                    // Guardar el estado de activación del perfil
                    setLoading(false);
                }
            } catch (error) {
                if (isMounted) {
                    console.error('Error al cargar datos:', error);
                    toast.error('Error al cargar los datos del proveedor');
                    navigate('/');
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [providerId, navigate]);


    // Función para actualizar formData (compatible con componentes originales)
    const updateFormData = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setHasUnsavedChanges(true);
        setTimeout(() => validateField(field, value), 0);
    }, [validateField]);

    // Función setFormData compatible con componentes originales
    const setFormDataCompat = useCallback((newDataOrFunction) => {
        if (typeof newDataOrFunction === 'function') {
            setFormData(prev => {
                const newData = newDataOrFunction(prev);
                setHasUnsavedChanges(true);
                return newData;
            });
        } else {
            setFormData(newDataOrFunction);
            setHasUnsavedChanges(true);
        }
    }, []);

    // Validar paso currentStep
    const validateCurrentStep = useCallback(() => {
        if (!formData || Object.keys(formData).length === 0) return false;

        const step = STEPS[currentStep];
        let isValid = true;

        switch (step.id) {
            case 'basic':
                isValid = !(!formData.name || formData.name.trim() === "" || formData.name.length > 100) &&
                    !(!formData.description || formData.description.trim() === "" || formData.description.length > 1000);
                break;
            case 'contact':
                isValid = !(!formData.phone || formData.phone.trim() === "" || !/^\d+$/.test(formData.phone) || formData.phone.length < 8 || formData.phone.length > 15) &&
                    !(!formData.locations || formData.locations.length === 0);
                break;
            case 'categories':
                isValid = !(!formData.categories || formData.categories.length === 0);
                break;
            case 'media':
                isValid = !(!formData.photo || formData.photo.trim() === "");
                break;
            default:
                isValid = true;
        }

        return isValid;
    }, [currentStep, formData]);

    // Navegación entre pasos
    const nextStep = useCallback(() => {
        if (validateCurrentStep() && currentStep < STEPS.length - 1) {
            setCurrentStep(prev => prev + 1);
        }
    }, [currentStep, validateCurrentStep]);

    const prevStep = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    }, [currentStep]);

    const goToStep = useCallback((stepIndex) => {
        setCurrentStep(stepIndex);
    }, []);

    // Función para hacer scroll al primer campo con error
    const scrollToFirstError = useCallback(() => {
        setTimeout(() => {
            const errorElement = document.querySelector('.form-input.error, .form-textarea.error, .react-select__control--is-focused');
            if (errorElement) {
                errorElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });
            }
        }, 100);
    }, []);

    // Función para hacer scroll al primer campo del paso actual
    const scrollToFirstField = useCallback(() => {
        setTimeout(() => {
            // Hacer scroll hasta arriba de todo de forma simple
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }, 200);
    }, []);

    // Efecto para scroll automático cuando cambia el paso
    useEffect(() => {
        scrollToFirstField();
    }, [currentStep, scrollToFirstField]);

    // Efecto para scroll automático cuando aparecen errores
    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            scrollToFirstError();
        }
    }, [errors, scrollToFirstError]);

    // Guardado manual final
    const handleFinalSave = useCallback(async () => {
        if (Object.keys(errors).length > 0) {
            toast.error("Por favor corrige los errores antes de guardar");
            return;
        }

        setSaving(true);
        try {
            const updatedFields = {};
            for (const key in formData) {
                if (JSON.stringify(formData[key]) !== JSON.stringify(initialData[key])) {
                    updatedFields[key] = formData[key];
                }
            }

            if (Object.keys(updatedFields).length === 0) {
                toast.info('No hay cambios para guardar');
                // Navegar a la pantalla previa o usar fallback
                if (previousPage) {
                    navigate(previousPage);
                } else if (isActive) {
                    navigate(`/${initialData.username}`);
                } else {
                    navigate(`/usuario/perfil`);
                }
                return;
            }

            // 🎯 Preparar datos para backend (maneja migración)
            const preparedUpdates = prepareProviderDataForBackend(updatedFields, true, categories); // true = updating existing, categories = available categories

            await updateProviderProfile(initialData.username, initialData.type, preparedUpdates);
            toast.success('¡Perfil actualizado exitosamente!');
            setHasUnsavedChanges(false);

            // Navegar a la pantalla previa o usar fallback según el estado de activación
            if (previousPage) {
                navigate(previousPage);
            } else if (isActive) {
                navigate(`/${initialData.username}`);
            } else {
                navigate(`/usuario/perfil`);
            }
        } catch (error) {
            if (error.response?.status === 403) {
                toast.error('No tienes permiso para editar este proveedor');
            } else {
                toast.error('Error al guardar los cambios');
            }
            console.error('Error al guardar:', error);
        } finally {
            setSaving(false);
        }
    }, [errors, formData, initialData, navigate, isActive, previousPage, categories]);

    // Renderizar paso actual
    const renderCurrentStep = useMemo(() => {
        const stepProps = {
            formData,
            updateFormData,
            setFormData: setFormDataCompat,
            errors,
            categories
        };

        switch (STEPS[currentStep].id) {
            case 'basic':
                return <BasicInfoStep {...stepProps} />;
            case 'contact':
                return <ContactStep {...stepProps} isEditing={true} />;
            case 'categories':
                return <CategoriesStep {...stepProps} isEditing={true} />;
            case 'media':
                return <MediaStep {...stepProps} />;
            case 'preview':
                return <PreviewStep {...stepProps} onSave={handleFinalSave} saving={saving} />;
            default:
                return null;
        }
    }, [currentStep, formData, updateFormData, setFormDataCompat, errors, categories, handleFinalSave, saving]);

    if (loading) {
        return (
            <div className="edit-provider-loading">
                <Spinner />
                <p>Cargando datos del perfil...</p>
            </div>
        );
    }

    return (
        <div className="edit-provider-modern" data-provider-type={formData.type}>
            <div className="edit-header">
                <div className="edit-header-content">
                    <h1>Editar Perfil</h1>
                    <div className="edit-header-info">
                        <span className="provider-type">
                            {formData.type === "service" ? "Servicios" : "Productos"}
                        </span>
                        <span className="provider-username">@{formData.username}</span>
                    </div>
                </div>
                <ProgressIndicator
                    progress={profileProgress}
                    hasUnsavedChanges={hasUnsavedChanges}
                />
            </div>

            <div className="edit-content">
                <div className="edit-sidebar">
                    <StepNavigation
                        steps={STEPS}
                        currentStep={currentStep}
                        onStepClick={goToStep}
                        errors={errors}
                        formData={formData}
                    />
                </div>

                <div className="edit-main">
                    <div className="step-content">
                        {renderCurrentStep}
                    </div>

                    <div className="step-navigation">
                        <button
                            onClick={prevStep}
                            disabled={currentStep === 0}
                            className="btn btn-secondary"
                        >
                            ← Anterior
                        </button>

                        <div className="step-indicator">
                            {currentStep + 1} de {STEPS.length}
                        </div>

                        {currentStep < STEPS.length - 1 ? (
                            <button
                                onClick={nextStep}
                                disabled={!validateCurrentStep()}
                                className="btn btn-primary"
                                style={{ backgroundColor: validateCurrentStep() ? 'var(--primary-color)' : undefined }}
                            >
                                Siguiente →
                            </button>
                        ) : (
                            <button
                                onClick={() => {
                                    // Navegar a la pantalla previa o usar fallback según el estado de activación
                                    if (previousPage) {
                                        navigate(previousPage);
                                    } else if (isActive) {
                                        navigate(`/${initialData.username}`);
                                    } else {
                                        const userData = getUserData();
                                        navigate(`/usuario/perfil`);
                                    }
                                }}
                                className="btn btn-outline"
                            >
                                Cancelar
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditProviderProfile;
